'use client'

import { format } from 'date-fns'
import { cn } from 'lib/utils'
import { Calendar as CalendarIcon } from 'lucide-react'
import { Calendar } from 'primitives/base/calendar'
import { Button } from 'primitives/button'
import { Popover, PopoverContent, PopoverTrigger } from 'primitives/popover'
import { TimePicker } from 'primitives/time-picker'
import * as React from 'react'
import { useEffect, useState } from 'react'
import type { Matcher } from 'react-day-picker'
import type { TimePickerFormat } from './time-picker/time-picker-utils'

export interface DateTimePickerProps {
  showSeconds?: boolean
  timeFormat?: TimePickerFormat
  onChange?: (date: Date | undefined) => void
  placeholder?: string
  disabled?: boolean
  defaultValue?: Date
  className?: string
  datesExcluded?: Matcher | Matcher[] | undefined
}

export const DateTimePicker = ({
  showSeconds = false,
  timeFormat = '12hours',
  onChange,
  placeholder = 'Select date & time',
  disabled = false,
  defaultValue,
  className = '',
  datesExcluded,
}: DateTimePickerProps) => {
  const [date, setDate] = useState<Date | undefined>(defaultValue)
  const [month, setMonth] = useState<Date | undefined>(defaultValue || new Date())
  const [popoverPosition, setPopoverPosition] = useState<'bottom' | 'top'>('bottom')
  const triggerRef = React.useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (date) setMonth(date)
  }, [date])

  // Calculate available space and set popover position
  const updatePopoverPosition = () => {
    if (!triggerRef.current) return

    const triggerRect = triggerRef.current.getBoundingClientRect()
    const viewportHeight = window.innerHeight
    const spaceBelow = viewportHeight - triggerRect.bottom
    const spaceAbove = triggerRect.top
    const requiredSpace = 480 // Approximate height of calendar + time picker

    setPopoverPosition(spaceBelow >= requiredSpace || spaceBelow >= spaceAbove ? 'bottom' : 'top')
  }

  const onPopoverClose = (open: boolean) => {
    if (!open) {
      onChange?.(date)
    }
  }

  return (
    <Popover onOpenChange={onPopoverClose}>
      <PopoverTrigger asChild>
        <Button
          ref={triggerRef}
          variant="outline"
          className={cn(
            'w-full justify-start text-left font-inter text-s-400 normal-case tracking-normal',
            !date && 'text-muted-foreground',
            disabled && 'bg-muted text-teritary',
            className,
          )}
          disabled={disabled}
          onClick={updatePopoverPosition}
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          <div className="truncate">
            {date ? (
              format(date, showSeconds ? 'PPP HH:mm:ss' : 'PPP HH:mm')
            ) : (
              <span>{placeholder}</span>
            )}
          </div>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0 " align="start" side={popoverPosition} sideOffset={4}>
        <Calendar
          mode="single"
          captionLayout="dropdown-buttons"
          selected={date}
          onSelect={(day) => day && setDate(day)}
          defaultMonth={month}
          onMonthChange={setMonth}
          initialFocus
          disabled={datesExcluded}
          fromYear={1900}
          toYear={2200}
        />
        <div className="p-3 border-t border-border">
          <TimePicker
            setDate={setDate}
            date={date}
            showSeconds={showSeconds}
            timeFormat={timeFormat}
          />
        </div>
      </PopoverContent>
    </Popover>
  )
}
